import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { FileText, Plus, Check, Send, Download } from 'lucide-react';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ChallanPreparation = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [challans, setChallans] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [form, setForm] = useState({ challan_type: 'pf', period: new Date().toISOString().slice(0, 7), due_date: '', state: '' });
  const [regenerating, setRegenerating] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data); setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchChallans = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/statutory/challans?company_id=${selectedCompany}`, { withCredentials: true });
      setChallans(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchChallans(); }, [selectedCompany, fetchChallans]);



  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API_URL}/statutory/challans`, { ...form, company_id: selectedCompany }, { withCredentials: true });
      setShowModal(false);
      fetchChallans();
      setSelectedChallan(data);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/statutory/challans/${id}`, { status }, { withCredentials: true });
      fetchChallans();
      if (selectedChallan?.id === id) {
        const { data } = await axios.get(`${API_URL}/statutory/challans/${id}`, { withCredentials: true });
        setSelectedChallan(data);
      }
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const statusColors = { draft: 'bg-slate-100 text-slate-600', prepared: 'bg-blue-100 text-blue-700', submitted: 'bg-emerald-100 text-emerald-700', verified: 'bg-green-100 text-green-700' };
  const typeColors = { pf: 'bg-blue-100 text-blue-700', esi: 'bg-emerald-100 text-emerald-700', pt: 'bg-amber-100 text-amber-700', lwf: 'bg-orange-100 text-orange-700', tds: 'bg-red-100 text-red-700' };

  return (
    <MainLayout>
      <div data-testid="challan-preparation-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Challan Preparation</h1>
            <p className="text-sm text-slate-500 mt-1">Prepare and track PF, ESI, PT, TDS challans</p>
          </div>
          <div className="flex items-center space-x-3">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button data-testid="new-challan-btn" onClick={() => setShowModal(true)} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC]"><Plus className="w-4 h-4" /><span>New Challan</span></button>
            <button data-testid="challans-export-btn" onClick={() => {
              if (challans.length === 0) { toast.error('No challans to export'); return; }
              downloadCSV(challans.map(c => ({
                Type: c.challan_type, Period: c.period, Due_Date: c.due_date || '',
                Total_Amount: c.total_amount || 0, Status: c.status, Reference: c.reference_number || ''
              })), `challans_export.csv`);
              toast.success('Challans exported');
            }} className="flex items-center space-x-2 bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-700">
              <Download className="w-4 h-4" /><span>Export</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-3">
            <h2 className="text-sm font-semibold text-slate-700">Challans</h2>
            {challans.map((c) => (
              <div key={c.id} data-testid={`challan-card-${c.id}`} onClick={() => setSelectedChallan(c)}
                className={`bg-white rounded-xl border p-4 cursor-pointer transition-colors ${selectedChallan?.id === c.id ? 'border-[#0052FF] bg-blue-50/30' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColors[c.challan_type] || 'bg-slate-100'}`}>{c.challan_type?.toUpperCase()}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[c.status] || 'bg-slate-100'}`}>{c.status}</span>
                </div>
                <p className="text-sm font-medium text-slate-800">Period: {c.period}</p>
                <p className="text-xs text-slate-500">Due: {c.due_date}</p>
                <p className="text-sm font-bold text-slate-900 mt-1">{(c.grand_total || c.total_pt || c.total_tds || 0).toLocaleString()}</p>
              </div>
            ))}
            {challans.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No challans yet</p>}
          </div>

          <div className="lg:col-span-2">
            {selectedChallan ? (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-slate-800">{selectedChallan.challan_type?.toUpperCase()} Challan - {selectedChallan.period}</h2>
                    <p className="text-xs text-slate-500">Due: {selectedChallan.due_date} | Employees: {selectedChallan.employee_count}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {selectedChallan.status === 'draft' && <button onClick={() => handleUpdateStatus(selectedChallan.id, 'prepared')} className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg">Mark Prepared</button>}
                    {selectedChallan.status === 'prepared' && <button onClick={() => handleUpdateStatus(selectedChallan.id, 'submitted')} className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg flex items-center space-x-1"><Send className="w-3 h-3" /><span>Mark Submitted</span></button>}
                    {selectedChallan.status === 'submitted' && <button onClick={() => handleUpdateStatus(selectedChallan.id, 'verified')} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg flex items-center space-x-1"><Check className="w-3 h-3" /><span>Verify</span></button>}
                  </div>
                </div>

                {/* === DIAGNOSTIC BANNER — surfaces "why is this 0?" ===
                    Renders for PF / ESI challans when grand_total is 0 or no
                    hierarchy was built. Shows the backend's diagnostic messages
                    and exposes a Regenerate button that rebuilds the hierarchy
                    in place after upstream data is fixed. */}
                {(selectedChallan.challan_type === 'pf' || selectedChallan.challan_type === 'esi') &&
                 (selectedChallan.challan_diagnostic?.messages?.length > 0 ||
                  !selectedChallan.code_groups?.length ||
                  (selectedChallan.grand_total || 0) === 0) && (
                  <div className="mx-4 mt-3 mb-2 bg-amber-50 border border-amber-300 rounded-md p-3"
                       data-testid="challan-diagnostic-banner">
                    <div className="flex items-start gap-2">
                      <div className="text-amber-600 text-base">⚠</div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-amber-900 mb-1">
                          {(selectedChallan.grand_total || 0) === 0
                            ? `${selectedChallan.challan_type?.toUpperCase()} challan total is ₹0 — fix upstream data, then click Regenerate`
                            : 'Challan diagnostic'}
                        </p>
                        {selectedChallan.challan_diagnostic?.messages?.length > 0 ? (
                          <ul className="list-disc pl-5 space-y-1 text-[11px] text-amber-800">
                            {selectedChallan.challan_diagnostic.messages.map((m, i) => (
                              <li key={i}>{m}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-[11px] text-amber-800">
                            This challan has no establishment-code hierarchy
                            (likely created before the group-wise upgrade or
                            without a processed payroll run). Click <b>Regenerate</b> to rebuild it.
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <button data-testid="challan-regenerate-btn"
                            disabled={regenerating}
                            onClick={async () => {
                              try {
                                setRegenerating(true);
                                const { data } = await axios.post(
                                  `${API_URL}/statutory/challans/${selectedChallan.id}/regenerate`,
                                  {}, { withCredentials: true },
                                );
                                toast.success('Challan regenerated');
                                setSelectedChallan(data);
                                fetchChallans();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Regenerate failed');
                              } finally {
                                setRegenerating(false);
                              }
                            }}
                            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                            {regenerating ? 'Regenerating…' : '↻ Regenerate Hierarchy'}
                          </button>
                          <span className="text-[10px] text-amber-700">
                            Uses the latest processed payroll run for this period.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}


                {selectedChallan.challan_type === 'pf' && (
                  <div className="p-4">
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-blue-50 rounded-lg p-3 text-center"><p className="text-xs text-blue-500">Employee PF</p><p className="text-lg font-bold text-blue-700">{selectedChallan.total_employee_pf?.toLocaleString()}</p></div>
                      <div className="bg-indigo-50 rounded-lg p-3 text-center"><p className="text-xs text-indigo-500">Employer PF + EPS</p><p className="text-lg font-bold text-indigo-700">{((selectedChallan.total_employer_pf || 0) + (selectedChallan.total_eps || 0)).toLocaleString()}</p></div>
                      <div className="bg-slate-50 rounded-lg p-3 text-center"><p className="text-xs text-slate-500">Grand Total</p><p className="text-lg font-bold text-slate-900">{selectedChallan.grand_total?.toLocaleString()}</p></div>
                    </div>
                  </div>
                )}
                {selectedChallan.challan_type === 'esi' && (
                  <div className="p-4 grid grid-cols-3 gap-4">
                    <div className="bg-emerald-50 rounded-lg p-3 text-center"><p className="text-xs text-emerald-500">Employee ESI</p><p className="text-lg font-bold text-emerald-700">{selectedChallan.total_employee_esi?.toLocaleString()}</p></div>
                    <div className="bg-teal-50 rounded-lg p-3 text-center"><p className="text-xs text-teal-500">Employer ESI</p><p className="text-lg font-bold text-teal-700">{selectedChallan.total_employer_esi?.toLocaleString()}</p></div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center"><p className="text-xs text-slate-500">Grand Total</p><p className="text-lg font-bold text-slate-900">{selectedChallan.grand_total?.toLocaleString()}</p></div>
                  </div>
                )}

                {/* === GROUP-WISE HIERARCHY (PF / ESI) ============================
                    Renders: Establishment Code → CTC Group → Employee, with
                    a subtotal per group and a CHALLAN TOTAL per establishment
                    code. This is the structure the user files with EPFO/ESIC. */}
                {selectedChallan.code_groups && selectedChallan.code_groups.length > 0 && (
                  <div className="border-t border-slate-100" data-testid="challan-hierarchy">
                    <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-blue-900">
                        📋 Group-wise Breakdown ·
                        <span className="ml-1 font-normal">
                          {selectedChallan.challan_summary?.establishment_codes || selectedChallan.code_groups.length} establishment code(s)
                        </span>
                        {selectedChallan.challan_summary?.unassigned_employees > 0 && (
                          <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded font-medium">
                            ⚠ {selectedChallan.challan_summary.unassigned_employees} employee(s) without {selectedChallan.challan_type?.toUpperCase()} Code
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center gap-2">
                        <a href={`${API_URL}/statutory/challans/${selectedChallan.id}/csv`}
                           data-testid="challan-csv-btn"
                           className="flex items-center gap-1 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded-md text-xs font-medium">
                          <Download className="w-3.5 h-3.5" /> CSV
                        </a>
                        <a href={`${API_URL}/statutory/challans/${selectedChallan.id}/pdf`}
                           data-testid="challan-pdf-btn"
                           className="flex items-center gap-1 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-md text-xs font-medium">
                          <Download className="w-3.5 h-3.5" /> PDF
                        </a>
                      </div>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {selectedChallan.code_groups.map((codeBlock) => (
                        <div key={codeBlock.code} className="p-4" data-testid={`code-block-${codeBlock.code}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-xs uppercase text-slate-500 tracking-wide">
                                {selectedChallan.challan_type === 'pf' ? 'PF Establishment Code' : 'ESI Sub-Code'}
                              </p>
                              <p className="text-base font-semibold text-slate-900 font-mono">{codeBlock.code}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-slate-500">{codeBlock.totals.headcount} employees</p>
                              <p className="text-lg font-bold text-blue-700">
                                ₹{(codeBlock.totals.challan_total || 0).toLocaleString('en-IN')}
                              </p>
                            </div>
                          </div>

                          {/* === EPFO A/c-wise breakdown chips — PF only.
                              Matches the rows the user types into the EPFO Unified
                              Portal challan: A/c 1 / A/c 2 / A/c 10 / A/c 21. === */}
                          {selectedChallan.challan_type === 'pf' && codeBlock.totals.ac1_employee !== undefined && (
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3 text-[11px]"
                                 data-testid={`pf-accounts-${codeBlock.code}`}>
                              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
                                <p className="text-blue-700 font-medium">A/c 1 — EE (12%)</p>
                                <p className="text-sm font-bold text-blue-900">₹{(codeBlock.totals.ac1_employee || 0).toLocaleString('en-IN')}</p>
                              </div>
                              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
                                <p className="text-blue-700 font-medium">A/c 1 — ER (3.67%)</p>
                                <p className="text-sm font-bold text-blue-900">₹{(codeBlock.totals.ac1_employer || 0).toLocaleString('en-IN')}</p>
                              </div>
                              <div className="bg-violet-50 border border-violet-200 rounded p-2 text-center">
                                <p className="text-violet-700 font-medium">A/c 10 — EPS (8.33%)</p>
                                <p className="text-sm font-bold text-violet-900">₹{(codeBlock.totals.ac10_eps || 0).toLocaleString('en-IN')}</p>
                              </div>
                              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                                <p className="text-emerald-700 font-medium">A/c 21 — EDLI (0.5%)</p>
                                <p className="text-sm font-bold text-emerald-900">₹{(codeBlock.totals.ac21_edli || 0).toLocaleString('en-IN')}</p>
                              </div>
                              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
                                <p className="text-amber-700 font-medium">A/c 2 — Admin (0.5%, min ₹500)</p>
                                <p className="text-sm font-bold text-amber-900">₹{(codeBlock.totals.ac2_admin || 0).toLocaleString('en-IN')}</p>
                              </div>
                            </div>
                          )}
                          <div className="space-y-2">
                            {codeBlock.groups.map((grp) => (
                              <details key={grp.group_id || grp.group_code}
                                       className="bg-slate-50 rounded-lg border border-slate-200">
                                <summary className="cursor-pointer px-3 py-2 flex items-center justify-between text-sm font-medium hover:bg-slate-100">
                                  <span><span className="font-mono text-violet-700">{grp.group_code}</span>
                                    <span className="text-slate-500 ml-2 text-xs">{grp.group_name}</span></span>
                                  <span className="text-xs text-slate-600">
                                    {grp.totals.headcount} emp ·
                                    EE ₹{grp.totals.employee_contrib.toLocaleString('en-IN')} ·
                                    ER ₹{grp.totals.employer_contrib.toLocaleString('en-IN')} ·
                                    <b className="text-slate-800 ml-1">₹{(grp.totals.employee_contrib + grp.totals.employer_contrib).toLocaleString('en-IN')}</b>
                                  </span>
                                </summary>
                                <table className="w-full text-xs border-t border-slate-200">
                                  <thead className="bg-slate-100">
                                    <tr>
                                      <th className="px-2 py-1 text-left">Code</th>
                                      <th className="px-2 py-1 text-left">Name</th>
                                      <th className="px-2 py-1 text-left">
                                        {selectedChallan.challan_type === 'pf' ? 'UAN' : 'ESI No.'}
                                      </th>
                                      <th className="px-2 py-1 text-right">Wages</th>
                                      <th className="px-2 py-1 text-right">Employee ₹</th>
                                      <th className="px-2 py-1 text-right">Employer ₹</th>
                                      <th className="px-2 py-1 text-right">Total ₹</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-200">
                                    {grp.employees.map((emp) => (
                                      <tr key={emp.employee_id} className="hover:bg-blue-50">
                                        <td className="px-2 py-1 font-mono text-slate-700">{emp.employee_code}</td>
                                        <td className="px-2 py-1 text-slate-800">{emp.name}</td>
                                        <td className="px-2 py-1 font-mono text-slate-500">
                                          {selectedChallan.challan_type === 'pf' ? emp.uan_number : emp.esi_number}
                                        </td>
                                        <td className="px-2 py-1 text-right font-mono">{emp.wages.toLocaleString('en-IN')}</td>
                                        <td className="px-2 py-1 text-right font-mono">{emp.employee_contrib.toLocaleString('en-IN')}</td>
                                        <td className="px-2 py-1 text-right font-mono">{emp.employer_contrib.toLocaleString('en-IN')}</td>
                                        <td className="px-2 py-1 text-right font-mono font-semibold">
                                          {(emp.employee_contrib + emp.employer_contrib).toLocaleString('en-IN')}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </details>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(selectedChallan.details?.length > 0) && !selectedChallan.code_groups && (
                  <div className="overflow-x-auto border-t border-slate-100">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50"><tr>
                        <th className="px-3 py-2 text-left">Employee</th>
                        {selectedChallan.challan_type === 'pf' && <><th className="px-3 py-2 text-right">PF Wages</th><th className="px-3 py-2 text-right">EE PF</th><th className="px-3 py-2 text-right">ER PF</th><th className="px-3 py-2 text-right">EPS</th></>}
                        {selectedChallan.challan_type === 'esi' && <><th className="px-3 py-2 text-right">Gross</th><th className="px-3 py-2 text-right">EE ESI</th><th className="px-3 py-2 text-right">ER ESI</th></>}
                        {selectedChallan.challan_type === 'pt' && <><th className="px-3 py-2 text-right">Gross</th><th className="px-3 py-2 text-right">PT</th></>}
                      </tr></thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedChallan.details.map((d, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="px-3 py-2 text-slate-800">{d.employee_name}</td>
                            {selectedChallan.challan_type === 'pf' && <><td className="px-3 py-2 text-right font-mono">{d.pf_wages?.toLocaleString()}</td><td className="px-3 py-2 text-right font-mono">{d.employee_pf?.toLocaleString()}</td><td className="px-3 py-2 text-right font-mono">{d.employer_pf?.toLocaleString()}</td><td className="px-3 py-2 text-right font-mono">{d.eps?.toLocaleString()}</td></>}
                            {selectedChallan.challan_type === 'esi' && <><td className="px-3 py-2 text-right font-mono">{d.gross_wages?.toLocaleString()}</td><td className="px-3 py-2 text-right font-mono">{d.employee_esi?.toLocaleString()}</td><td className="px-3 py-2 text-right font-mono">{d.employer_esi?.toLocaleString()}</td></>}
                            {selectedChallan.challan_type === 'pt' && <><td className="px-3 py-2 text-right font-mono">{d.gross_wages?.toLocaleString()}</td><td className="px-3 py-2 text-right font-mono">{d.pt_amount?.toLocaleString()}</td></>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-base font-semibold text-slate-600">Select a Challan</h3>
                <p className="text-sm text-slate-400 mt-1">Choose a challan from the list to view details</p>
              </div>
            )}
          </div>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">New Challan</h3>
              <form onSubmit={handleCreate} className="space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select data-testid="challan-type-select" value={form.challan_type} onChange={(e) => setForm({ ...form, challan_type: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    <option value="pf">PF</option><option value="esi">ESI</option><option value="pt">PT</option><option value="lwf">LWF</option><option value="tds">TDS</option>
                  </select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Period</label>
                  <input type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                  <input type="date" data-testid="challan-due-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="create-challan-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC]">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ChallanPreparation;
